import {
  IPriceUnderProductSchemaUI,
  IQuoteItemRespSchema,
  IQuoteItemWithDisplayType,
  IQuoteOfferingRespSchema,
  IRateResBaseSchema,
  IRateResSchema,
  PriceUpliftCalculatorConfig,
  ProductTypeEnum,
} from '@monetize/types/app';
import { Maybe } from '@monetize/types/common';
import {
  roundNumberToDecimal,
  sortByProductPosition,
} from '@monetize/utils/core';
import isEmpty from 'lodash/isEmpty';
import { ONE_TIME_PRODUCT_TYPES } from '../constants/products';
import { QuoteOfferingForUplift } from '../types';
import { getOrderedPricesUnderProduct } from './product';

/**
 * Creates an uplift calculator object from the provided props
 * This will drive the UI
 */
export const createUpliftCalculatorObj = (
  offeringRatesById: Record<string, IRateResBaseSchema>,
  quoteOfferingData: IQuoteOfferingRespSchema,
  childQuoteOfferingsData: IQuoteOfferingRespSchema[] = [],
): PriceUpliftCalculatorConfig => {
  return {
    parentQuoteOffering: {
      id: quoteOfferingData.id,
      quoteId: quoteOfferingData.quoteId,
      rateId: quoteOfferingData.rateId,
      startDate: quoteOfferingData.startDate,
      endDate: quoteOfferingData.endDate,
      parentQuoteOfferingId: quoteOfferingData.parentQuoteOfferingId,
      offeringId: quoteOfferingData.offeringId,
      priceUpliftConfigurationId: quoteOfferingData.priceUpliftConfigurationId,
      items: sortByProductPosition(quoteOfferingData.items ?? [])
        .filter(({ productType }) => !ONE_TIME_PRODUCT_TYPES.has(productType))
        .map((item) => {
          if (
            item.productType === ProductTypeEnum.USAGE &&
            !isEmpty(offeringRatesById)
          ) {
            const prices = getUsageProductPriceForUplift(
              quoteOfferingData.rateId,
              offeringRatesById,
              item.productId,
            );
            return {
              ...item,
              ...prices,
            };
          }
          return item;
        }),
    },
    childQuoteOfferings: childQuoteOfferingsData.map((segment) => ({
      id: segment.id,
      quoteId: segment.quoteId,
      rateId: segment.rateId,
      startDate: segment.startDate,
      endDate: segment.endDate,
      parentQuoteOfferingId: segment.parentQuoteOfferingId,
      offeringId: segment.offeringId,
      priceUpliftConfigurationId: segment.priceUpliftConfigurationId,
      items: sortByProductPosition(segment.items ?? [])
        .filter(({ productType }) => !ONE_TIME_PRODUCT_TYPES.has(productType))
        .map((item) => {
          if (
            item.productType === ProductTypeEnum.USAGE &&
            !isEmpty(offeringRatesById)
          ) {
            const prices = getUsageProductPriceForUplift(
              segment.rateId,
              offeringRatesById,
              item.productId,
            );
            return {
              ...item,
              ...prices,
            };
          }
          return item;
        }),
    })),
  };
};

/**
 * Builds a map of original items for efficient lookup during uplift calculations.
 *
 */
export const buildOriginalItemsMapBySegment = (
  childQuoteOfferings: IQuoteOfferingRespSchema[],
  offeringRatesById?: Record<string, IRateResBaseSchema>,
  parentQuoteOffering?: IQuoteOfferingRespSchema | null,
): Record<string, Record<string, IQuoteItemWithDisplayType>> => {
  const newMap: Record<string, Record<string, IQuoteItemWithDisplayType>> = {};

  if (parentQuoteOffering) {
    const parentSegmentMap: Record<string, IQuoteItemWithDisplayType> = {};

    sortByProductPosition(parentQuoteOffering.items ?? [])
      .filter(({ productType }) => !ONE_TIME_PRODUCT_TYPES.has(productType))
      .forEach((item) => {
        if (item.productType === ProductTypeEnum.USAGE && offeringRatesById) {
          const prices = getUsageProductPriceForUplift(
            parentQuoteOffering.rateId,
            offeringRatesById,
            item.productId,
          );
          parentSegmentMap[item.productId] = {
            ...item,
            ...prices,
          };
        } else {
          parentSegmentMap[item.productId] = item;
        }
      });
    newMap['parent'] = parentSegmentMap;
  }

  childQuoteOfferings.forEach((offering) => {
    const segmentMap: Record<string, IQuoteItemWithDisplayType> = {};

    sortByProductPosition(offering.items ?? [])
      .filter(({ productType }) => !ONE_TIME_PRODUCT_TYPES.has(productType))
      .forEach((item) => {
        if (item.productType === ProductTypeEnum.USAGE && offeringRatesById) {
          const prices = getUsageProductPriceForUplift(
            offering.rateId,
            offeringRatesById,
            item.productId,
          );
          segmentMap[item.productId] = {
            ...item,
            ...prices,
          };
        } else {
          segmentMap[item.productId] = item;
        }
      });
    newMap[offering.id] = segmentMap;
  });

  return newMap;
};

/**
 * Multiplies every price field on an item by `multiplier`.
 *
 * Callers rebase the item onto its base segment before calling this. The
 * cascade re-runs on every edit, so any field multiplied here that is not
 * rebased compounds across passes instead of being recalculated.
 */
export const applyUpliftToItem = (
  item: IQuoteItemWithDisplayType,
  multiplier: number,
): IQuoteItemWithDisplayType => {
  return {
    ...item,
    displayUnitPrice: item.displayUnitPrice * multiplier,
    displayUnitPriceAfterDiscount:
      item.displayUnitPriceAfterDiscount * multiplier,
    unitPrice: item.unitPrice * multiplier,
    unitPriceAfterDiscount: item.unitPriceAfterDiscount * multiplier,
    customPrice: item.customPrice
      ? {
          ...item.customPrice,
          amount: item.customPrice.amount * multiplier,
        }
      : null,
  };
};

/**
 * Calculates and applies cascading uplift
 * where each segment's uplift is applied to the previous segment's uplifted value.
 */
export const calculateUpliftForAllItemsInAllSegments = (
  childOfferings: QuoteOfferingForUplift[],
  updatedSegmentUpliftMap: Map<string, { upliftAmount: number }>,
  updatedProductUpliftMap: Map<string, Map<string, { upliftAmount: number }>>,
  originalItemsMapBySegment: Record<
    string,
    Record<string, IQuoteItemWithDisplayType>
  >,
) => {
  const parentItems = originalItemsMapBySegment['parent'] || {};

  // Process each segment sequentially to implement cascading logic
  for (
    let segmentIndex = 0;
    segmentIndex < childOfferings.length;
    segmentIndex++
  ) {
    const offering = childOfferings[segmentIndex];
    const segmentKey = segmentIndex.toString();
    const productUplifts = updatedProductUpliftMap.get(segmentKey);
    const segmentUplift = updatedSegmentUpliftMap.get(segmentKey);
    const hasProductUplifts = productUplifts && productUplifts.size > 0;
    const hasSegmentUplift = segmentUplift && segmentUplift.upliftAmount >= 0;

    // first segment with no uplifts should be skipped
    if (segmentIndex === 0 && !hasProductUplifts && !hasSegmentUplift) {
      continue;
    }

    // Determine the base values for this segment
    let baseItemsMap: Record<string, IQuoteItemWithDisplayType>;
    if (segmentIndex === 0) {
      baseItemsMap = parentItems;
    } else {
      const previousOffering = childOfferings[segmentIndex - 1];
      baseItemsMap = {};

      // Convert previous segment's current items to base map
      previousOffering.items.forEach((item) => {
        baseItemsMap[item.productId] = item;
      });
    }

    const currentItemsMap: Record<string, IQuoteItemWithDisplayType> = {};
    offering.items.forEach((item) => {
      currentItemsMap[item.productId] = item;
    });

    // Calculate uplifted items for this segment
    const upliftedItems = Object.values(baseItemsMap).map((baseItem) => {
      const productUplift = productUplifts?.get(baseItem.productId);
      let upliftMultiplier = 1;

      if (productUplift) {
        upliftMultiplier = 1 + productUplift.upliftAmount / 100;
      } else if (hasSegmentUplift) {
        upliftMultiplier = 1 + segmentUplift.upliftAmount / 100;
      }

      const currentItem = currentItemsMap[baseItem.productId];

      if (currentItem) {
        const itemWithUpdatedBaseValues = {
          ...currentItem,
          displayUnitPrice: baseItem.displayUnitPrice,
          displayUnitPriceAfterDiscount: baseItem.displayUnitPriceAfterDiscount,
          unitPrice: baseItem.unitPrice,
          unitPriceAfterDiscount: baseItem.unitPriceAfterDiscount,
          customPrice:
            currentItem.customPrice && baseItem.customPrice
              ? {
                  ...currentItem.customPrice,
                  amount: baseItem.customPrice.amount,
                }
              : currentItem.customPrice,
        };

        return applyUpliftToItem(itemWithUpdatedBaseValues, upliftMultiplier);
      }

      return applyUpliftToItem(baseItem, upliftMultiplier);
    });

    childOfferings[segmentIndex] = {
      ...offering,
      items: upliftedItems,
    };
  }
};

/**
 * Calculates and applies product-specific uplifts to a single product across all segments.
 */
export const calculateUpliftForSingleItemInAllSegments = (
  childOfferings: QuoteOfferingForUplift[],
  updatedProductUpliftMap: Map<string, Map<string, { upliftAmount: number }>>,
  productId: string,
  currentSegmentUpliftMap: Map<string, { upliftAmount: number }>,
  originalItemsMapBySegment: Record<
    string,
    Record<string, IQuoteItemWithDisplayType>
  >,
) => {
  const parentItems = originalItemsMapBySegment['parent'] || {};
  const parentItem = parentItems[productId];

  // Process each segment sequentially to implement cascading logic
  for (
    let segmentIndex = 0;
    segmentIndex < childOfferings.length;
    segmentIndex++
  ) {
    const offering = childOfferings[segmentIndex];
    const segmentKey = segmentIndex.toString();
    const productUplifts = updatedProductUpliftMap.get(segmentKey);
    const productUplift = productUplifts?.get(productId);
    const segmentUplift = currentSegmentUpliftMap.get(segmentKey);
    const totalUpliftPercentage =
      (segmentUplift?.upliftAmount ?? 0) + (productUplift?.upliftAmount ?? 0);

    const itemIndex = offering.items.findIndex(
      (item) => item.productId === productId,
    );

    let baseItem: IQuoteItemWithDisplayType;
    if (segmentIndex === 0) {
      baseItem = parentItem;
    } else {
      const previousOffering = childOfferings[segmentIndex - 1];
      const previousItem = previousOffering.items.find(
        (item) => item.productId === productId,
      );

      if (!previousItem) {
        baseItem = parentItem;
      } else {
        baseItem = previousItem;
      }
    }

    const upliftMultiplier = 1 + totalUpliftPercentage / 100;
    const currentItem = offering.items[itemIndex];
    const itemWithUpdatedBaseValues = {
      ...currentItem,
      displayUnitPrice: baseItem.displayUnitPrice,
      displayUnitPriceAfterDiscount: baseItem.displayUnitPriceAfterDiscount,
      unitPrice: baseItem.unitPrice,
      unitPriceAfterDiscount: baseItem.unitPriceAfterDiscount,
      customPrice:
        currentItem.customPrice && baseItem.customPrice
          ? {
              ...currentItem.customPrice,
              amount: baseItem.customPrice.amount,
            }
          : currentItem.customPrice,
    };

    const upliftedItem = applyUpliftToItem(
      itemWithUpdatedBaseValues,
      upliftMultiplier,
    );

    // Update the item in the offering
    const upliftedItems = [...offering.items];
    upliftedItems[itemIndex] = upliftedItem;
    offering.items = upliftedItems;
  }
};

/**
 * Collects all unique product IDs that have individual uplifts across all segments
 */
export const collectProductsWithUplifts = (
  productUpliftMap: Map<string, Map<string, { upliftAmount: number }>>,
): Set<string> => {
  const productsWithUplifts = new Set<string>();
  for (const productUplifts of productUpliftMap.values()) {
    for (const productId of productUplifts.keys()) {
      productsWithUplifts.add(productId);
    }
  }
  return productsWithUplifts;
};

export const getUsageProductPriceForUplift = (
  rateId: Maybe<string>,
  offeringRatesById: Record<string, IRateResBaseSchema>,
  productId: string,
): {
  displayUnitPrice: number;
  unitPrice: number;
  unitPriceAfterDiscount: number;
} => {
  const rate = rateId
    ? (offeringRatesById[rateId] as IRateResSchema | undefined)
    : undefined;

  // A rate that never loaded (failed fetch, or an offering with no rateId)
  // leaves its usage prices unknown — the PPU column renders these zeros as the
  // usage-determined `*` marker rather than taking down the caller.
  if (!rate) {
    return {
      displayUnitPrice: 0,
      unitPrice: 0,
      unitPriceAfterDiscount: 0,
    };
  }

  const rateData = getOrderedPricesUnderProduct(rate);

  const usageRate = rateData.find(({ id }) => id === productId);

  if (usageRate?.prices && usageRate.prices.length > 1) {
    return {
      displayUnitPrice: 0,
      unitPrice: 0,
      unitPriceAfterDiscount: 0,
    };
  } else {
    return {
      displayUnitPrice: usageRate?.prices[0]?.amount ?? 0,
      unitPrice: usageRate?.prices[0]?.amount ?? 0,
      unitPriceAfterDiscount: usageRate?.prices[0]?.amount ?? 0,
    };
  }
};

export const getUsageProductPriceForUpliftPPU = (
  item: IQuoteItemRespSchema,
) => {
  if (!item.unitPriceAfterDiscount) {
    return '*';
  } else {
    return `${item.unitPriceAfterDiscount}`;
  }
};

const applySegmentUpliftToRates = (
  pricesToBeUplifted: IPriceUnderProductSchemaUI[],
  upliftMultiplier: number,
): void => {
  pricesToBeUplifted.forEach((product) => {
    if (!ONE_TIME_PRODUCT_TYPES.has(product.productType)) {
      product.prices.forEach((price) => {
        price.amount = roundNumberToDecimal(price.amount * upliftMultiplier, 6);
      });
    }
  });
};

/**
 * implements cascading uplift
 * where each segment's rates are based on the previous segment'suplifted rates
 * @param childQuoteOfferings - Array of child quote offerings
 * @param offeringRatesById - Map of offering rates by ID
 * @param segmentUpliftMap - Map of segment uplifts by offering index
 * @param productUpliftMap - Map of product-specific uplifts by offering index and product ID
 * @param parentOfferingRateId - Rate ID of the parent offering to use as base for first segment
 * @returns Map of offering index to uplifted prices under product
 */
export const calculateUpliftedRates = (
  childQuoteOfferings: QuoteOfferingForUplift[],
  offeringRatesById: Record<string, IRateResBaseSchema>,
  segmentUpliftMap: Map<string, { upliftAmount: number }>,
  productUpliftMap: Map<string, Map<string, { upliftAmount: number }>>,
  parentOfferingRateId: string,
): Map<string, IPriceUnderProductSchemaUI[]> => {
  const upliftedRatesMap = new Map<string, IPriceUnderProductSchemaUI[]>();

  for (
    let offeringIndex = 0;
    offeringIndex < childQuoteOfferings.length;
    offeringIndex++
  ) {
    const offering = childQuoteOfferings[offeringIndex];
    const offeringIndexStr = offeringIndex.toString();
    const segmentKey = offeringIndexStr;

    if (!offering.rateId) {
      continue;
    }

    const offeringRate = offeringRatesById[offering.rateId] as IRateResSchema;

    if (!offeringRate) {
      continue;
    }

    const productUplifts = productUpliftMap.get(segmentKey);
    const segmentUplift = segmentUpliftMap.get(segmentKey);
    const hasProductUplifts = productUplifts && productUplifts.size > 0;
    const hasSegmentUplift = segmentUplift && segmentUplift.upliftAmount >= 0;

    let baseRates: IPriceUnderProductSchemaUI[];
    if (offeringIndex === 0) {
      const parentOfferingRate = offeringRatesById[
        parentOfferingRateId
      ] as IRateResSchema;

      //If segment uplift: use parent's rate as base for all products
      if (hasSegmentUplift) {
        if (!parentOfferingRate) {
          // Fallback to current offering rates if parent rate not found
          const currentRates = getOrderedPricesUnderProduct(offeringRate);
          if (currentRates.length === 0) {
            continue;
          }
          baseRates = structuredClone(currentRates);
        } else {
          const parentRates = getOrderedPricesUnderProduct(parentOfferingRate);
          if (parentRates.length === 0) {
            continue;
          }
          baseRates = structuredClone(parentRates);
        }
      } else if (hasProductUplifts) {
        // If product uplift: use current rate as base, but parent's rate for specific uplifted products
        const currentRates = getOrderedPricesUnderProduct(offeringRate);

        if (currentRates.length === 0) {
          continue;
        }

        baseRates = structuredClone(currentRates);

        // Replace rates for products that have uplifts with parent rates
        if (parentOfferingRate) {
          const parentRates = getOrderedPricesUnderProduct(parentOfferingRate);
          const parentRatesMap = new Map(
            parentRates.map((rate) => [rate.id, rate]),
          );

          productUplifts.forEach((_, productId) => {
            const parentRate = parentRatesMap.get(productId);
            if (parentRate) {
              const baseRateIndex = baseRates.findIndex(
                (rate) => rate.id === productId,
              );
              if (baseRateIndex !== -1) {
                baseRates[baseRateIndex] = structuredClone(parentRate);
              }
            }
          });
        }
      } else {
        // No uplifts: use current offering rates as-is
        const currentRates = getOrderedPricesUnderProduct(offeringRate);

        if (currentRates.length === 0) {
          continue;
        }
        upliftedRatesMap.set(offeringIndexStr, structuredClone(currentRates));
        continue;
      }
    } else {
      // Subsequent segments: use previous segment's uplifted rates as base
      const previousOfferingIndexStr = (offeringIndex - 1).toString();
      const previousRates = upliftedRatesMap.get(previousOfferingIndexStr);
      if (!previousRates || previousRates.length === 0) {
        continue;
      }
      baseRates = structuredClone(previousRates);
    }

    const pricesToBeUplifted = baseRates;

    if (hasSegmentUplift) {
      const segmentMultiplier = 1 + segmentUplift.upliftAmount / 100;
      applySegmentUpliftToRates(pricesToBeUplifted, segmentMultiplier);
    }

    if (hasProductUplifts) {
      productUplifts.forEach((uplift, productId) => {
        const productToUplift = pricesToBeUplifted.find(
          (product) => product.id === productId,
        );

        if (productToUplift && uplift.upliftAmount !== 0) {
          const productMultiplier = 1 + uplift.upliftAmount / 100;
          productToUplift.prices.forEach((price) => {
            price.amount = roundNumberToDecimal(
              price.amount * productMultiplier,
              6,
            );
          });
        }
      });
    }

    upliftedRatesMap.set(offeringIndexStr, pricesToBeUplifted);
  }

  return upliftedRatesMap;
};

/**
 * Calculates the uplift percentage between current and base prices
 * @param currentPrice - The current price after uplift
 * @param basePrice - The original base price
 * @returns The uplift percentage (e.g., 10 for 10% uplift)
 */
export const calculateUpliftPercentage = (
  currentPrice: number,
  basePrice: number,
): number => {
  if (basePrice === 0) {
    return 0;
  }
  const rawPercentage =
    roundNumberToDecimal((currentPrice - basePrice) / basePrice, 2) * 100;
  return roundNumberToDecimal(rawPercentage, 2);
};

/**
 * Back-calculates uplift percentages for all items in a segment
 * @param segment - The current segment with uplifted prices
 * @param baseItems - The base items to compare against (parent or previous segment)
 * @returns Object with segment-wide and product-specific uplift data
 */
export const backCalculateUpliftPercentages = (
  segment: QuoteOfferingForUplift,
  baseItems: IQuoteItemWithDisplayType[],
): {
  segmentUpliftPercentage: number | null;
  productUplifts: Array<{ productId: string; upliftPercentage: number }>;
  isUniformUplift: boolean;
} => {
  const productUplifts: Array<{ productId: string; upliftPercentage: number }> =
    [];
  const upliftPercentages: number[] = [];

  segment.items.forEach((currentItem) => {
    const baseItem = baseItems.find(
      (item) => item.productId === currentItem.productId,
    );

    if (baseItem) {
      // Using unitPriceAfterDiscount as it's the most accurate price for comparison
      const currentPrice =
        currentItem.unitPriceAfterDiscount || currentItem.unitPrice;
      const basePrice = baseItem.unitPriceAfterDiscount || baseItem.unitPrice;

      const upliftPercentage = calculateUpliftPercentage(
        currentPrice,
        basePrice,
      );

      productUplifts.push({
        productId: currentItem.productId,
        upliftPercentage,
      });

      upliftPercentages.push(upliftPercentage);
    }
  });

  const isUniformUplift =
    upliftPercentages.length > 0 &&
    upliftPercentages.every(
      (percentage) => Math.abs(percentage - upliftPercentages[0]) < 0.01, // Allow for small rounding differences
    );

  const segmentUpliftPercentage = isUniformUplift ? upliftPercentages[0] : null;

  return {
    segmentUpliftPercentage,
    productUplifts,
    isUniformUplift,
  };
};
